/**
 * CSS 変換による 2D めくりエンジン（FR-FLIP-02〜03）。three.js には依存しない。
 *
 * 構造: stage（透視）> book（見開きの箱）> 左右のページ面 ＋ めくり中のシート。
 * めくりは 1 枚のシートを背表紙側を軸に rotateY させ、表＝出発ページ・裏＝着地ページとする。
 */
import { cls } from '../../brand';
import type { EngineContext, EngineContextPatch, RenderEngine, Viewport } from '../../core/engine';
import type { PageRaster } from '../../core/source';
import { MAX_CANVAS_AREA, MAX_CANVAS_SIDE } from '../../source/canvas-limits';
import {
  DETAIL_DELAY_MS,
  DETAIL_MIN_ZOOM,
  MAX_DPR,
  REGION_MARGIN,
  REGION_STEP,
  WINDOW_PAGES,
} from '../constants';
import {
  fitPageBox,
  flipPlan,
  pageScreenRect,
  pageSize,
  pageSlot,
  pagesOf,
  resolveMode,
  spreadAt,
  spreadOf,
  singleFlipPlan,
  spreadShift,
} from '../layout';
import type { ScreenRect, Spread } from '../layout';
import { inflateRegion, isFullPage, pageRegion, planRaster, quantizeRegion, visibleBox } from '../region';
import { TextureStore } from '../texture-store';
import { centerOf, panForCenter, toPageLocal, type PageSlotRect } from '../viewport';

/** めくりの所要時間（ms）。reduced-motion では簡略遷移に切り替える。 */
const FLIP_MS = 700;
const REDUCED_MS = 120;

/** 1 ページ分の描画面。 */
class PageSlot {
  readonly el: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  /** 拡大時に可視領域だけを高解像度で焼いて重ねる面（下地は触らない）。 */
  private readonly detail: HTMLCanvasElement;
  /** 下地と領域ラスタでトークン列を分ける（片方の追い越しが他方の描画を落とさないように）。 */
  private seq = 0;
  private detailSeq = 0;

  constructor(doc: Document, modifier: string) {
    this.el = doc.createElement('div');
    this.el.className = `${cls('page')} ${cls(`page--${modifier}`)}`;
    this.canvas = doc.createElement('canvas');
    // 既定の 300x150 のピクセルバッファを持たせない（未描画は 0 とわかるようにする）。
    this.canvas.width = 0;
    this.canvas.height = 0;
    // 誌面の中身は canvas では支援技術に届かない。代替テキストは ui/page-text.ts が
    // 別に出すので、canvas 自体は装飾として伏せる（WCAG 1.1.1・M8c-2 手順 7）。
    this.canvas.setAttribute('aria-hidden', 'true');
    this.detail = doc.createElement('canvas');
    this.detail.className = cls('page-detail');
    this.detail.setAttribute('aria-hidden', 'true');
    this.detail.width = 0;
    this.detail.height = 0;
    this.detail.hidden = true;
    this.el.append(this.canvas, this.detail);
  }

  /** 領域ラスタの描画要求トークン（下地とは独立）。 */
  beginDetail(): number {
    this.detailSeq += 1;
    return this.detailSeq;
  }

  /** 領域ラスタを下地の上へ重ねる。位置と寸法はラスタが持つ領域を正とする。 */
  drawDetail(raster: PageRaster, token: number): void {
    if (token !== this.detailSeq) return;
    const { region } = raster;
    this.detail.width = raster.width;
    this.detail.height = raster.height;
    this.detail.getContext('2d')?.drawImage(raster.bitmap, 0, 0);
    this.detail.style.left = `${region.x * 100}%`;
    this.detail.style.top = `${region.y * 100}%`;
    this.detail.style.width = `${region.w * 100}%`;
    this.detail.style.height = `${region.h * 100}%`;
    this.detail.hidden = false;
  }

  /** 領域ラスタを外す（めくり中・等倍付近では常にこれを呼ぶ）。 */
  clearDetail(): void {
    this.detailSeq += 1; // 実行中の焼き上がりが後から重ならないようにする
    this.detail.hidden = true;
    this.detail.width = 0;
    this.detail.height = 0;
  }

  /** 描画要求のトークンを発行する（以後、古いトークンの draw は無視される）。 */
  begin(): number {
    this.seq += 1;
    return this.seq;
  }

  /** 空ページ（対向なし）にする。 */
  clear(): void {
    this.seq += 1;
    this.el.classList.add(cls('page--empty'));
    this.canvas.width = 0;
    this.canvas.height = 0;
    this.clearDetail();
  }

  /**
   * ラスタを描き込む。追い越された結果（stale）は捨てる。
   * ラスタの所有者は TextureStore なので、ここでは複製して DOM 側と寿命を切り離す。
   */
  draw(raster: PageRaster, token: number): void {
    if (token !== this.seq) return;
    this.el.classList.remove(cls('page--empty'));
    this.canvas.width = raster.width;
    this.canvas.height = raster.height;
    this.canvas.getContext('2d')?.drawImage(raster.bitmap, 0, 0);
  }

  resize(width: number, height: number): void {
    this.el.style.width = `${width}px`;
    this.el.style.height = `${height}px`;
  }

  /** 表紙・裏表紙かどうか（hardcover 表現の対象）。 */
  setCover(isCover: boolean): void {
    this.el.classList.toggle(cls('page--cover'), isCover);
  }
}

export class Css2dEngine implements RenderEngine {
  private context: EngineContext | null = null;
  private store: TextureStore | null = null;
  private doc: Document = document;

  private stage: HTMLDivElement | null = null;
  private book: HTMLDivElement | null = null;
  private left: PageSlot | null = null;
  private right: PageSlot | null = null;

  private current: Spread = { index: 0, left: null, right: null };
  private currentPage = 1;
  private mode: 'single' | 'spread' = 'single';
  private box = { width: 0, height: 0 };
  private zoom = 1;
  private pan = { x: 0, y: 0 };
  /** 対向ページが無いときに 1 枚を中央へ寄せる量。 */
  private shift = 0;
  private flip: Animation | null = null;
  /** 領域ラスタの遅延実行タイマ（ズーム・パンが落ち着いてから焼く）。 */
  private detailTimer: number | null = null;

  get spread(): boolean {
    return this.mode === 'spread';
  }

  pageRect(pageNo: number): ScreenRect | null {
    const host = this.stage?.parentElement;
    if (!host) return null;
    const slot = pageSlot(pageNo, this.mode, this.current, this.currentPage);
    if (!slot) return null;
    const container = { width: host.clientWidth, height: host.clientHeight };
    return pageScreenRect(container, this.box, slot, this.shift, this.zoom, this.pan);
  }

  mount(host: HTMLElement, context: EngineContext): void {
    this.context = context;
    this.doc = host.ownerDocument;
    this.store = new TextureStore((request) => context.raster(request));

    const stage = this.doc.createElement('div');
    stage.className = cls('stage');
    const book = this.doc.createElement('div');
    book.className = cls('book');
    this.left = new PageSlot(this.doc, 'left');
    this.right = new PageSlot(this.doc, 'right');
    book.append(this.left.el, this.right.el);
    stage.appendChild(book);
    host.appendChild(stage);

    this.stage = stage;
    this.book = book;
    this.layout();
  }

  async goTo(pageNo: number, options: { animate: boolean }): Promise<void> {
    const context = this.requireContext();
    const target = spreadAt(spreadOf(pageNo), context.pages.length, context.direction);
    const forward =
      target.index === this.current.index ? pageNo > this.currentPage : target.index > this.current.index;

    const from = this.current;
    const fromPage = this.currentPage;
    // 単ページ表示（スマートフォンなど）でもめくる — 見開きのときだけ動く実装だと、
    // スマートフォンでは常にアニメーションが出ない。
    const changed = this.mode === 'spread' ? target.index !== from.index : pageNo !== fromPage;
    const useFlip = options.animate && !context.reducedMotion && changed;

    // ページが変われば領域ラスタは無効（めくらない遷移でも同じ）
    this.cancelDetail();
    this.current = target;
    this.currentPage = pageNo;
    context.bus.emit('engine:flipStart', { direction: forward ? 'forward' : 'back' });

    if (useFlip) {
      await this.animateFlip(from, target, forward, fromPage);
    } else {
      await this.simpleTransition(options.animate && context.reducedMotion);
    }

    context.bus.emit('engine:flipEnd', { page: pageNo });
  }

  setZoom(level: number, focal?: { x: number; y: number }): void {
    this.zoom = level;
    if (level <= 1) {
      this.pan = { x: 0, y: 0 };
    } else if (focal) {
      // 拡大の中心が視野に残るよう、中心からのずれ分だけ逆向きに寄せる。
      this.pan = this.clampPan({
        x: (0.5 - focal.x) * (level - 1) * this.bookWidth(),
        y: (0.5 - focal.y) * (level - 1) * this.box.height,
      });
    } else {
      this.pan = this.clampPan(this.pan);
    }
    this.applyTransform();
    void this.paintCurrent();
  }

  panBy(dx: number, dy: number): void {
    if (this.zoom <= 1) return;
    this.pan = this.clampPan({ x: this.pan.x + dx, y: this.pan.y + dy });
    this.applyTransform();
    // 拡大中はパンで可視領域が変わる = 領域ラスタを焼き直す必要がある
    this.scheduleDetail();
  }

  getViewport(): Viewport | null {
    const container = this.container();
    if (!container) return null;
    const center = centerOf(container, this.bookBox(), this.zoom, this.pan, this.shift);
    const hit = toPageLocal(center, this.slotRects());
    return hit ? { ...hit, zoom: this.zoom } : null;
  }

  setViewport(viewport: Viewport): void {
    const slot = this.slotRects().find((entry) => entry.page === viewport.page);
    if (!slot || !this.container()) return;
    this.zoom = viewport.zoom;
    this.pan =
      viewport.zoom <= 1
        ? { x: 0, y: 0 }
        : this.clampPan(
            panForCenter(this.bookBox(), slot.rect, viewport.x, viewport.y, viewport.zoom, this.shift),
          );
    this.applyTransform();
    void this.paintCurrent();
    this.scheduleDetail();
  }

  resize(): void {
    this.cancelDetail();
    this.layout();
    void this.paintCurrent();
  }

  setContext(patch: EngineContextPatch): void {
    const context = this.requireContext();
    Object.assign(context, patch);
    this.cancelDetail();
    this.current = spreadAt(spreadOf(this.currentPage), context.pages.length, context.direction);
    this.layout();
    void this.paintCurrent();
  }

  unmount(): void {
    this.cancelDetail();
    this.flip?.cancel();
    this.flip = null;
    this.stage?.remove();
    this.stage = null;
    this.book = null;
  }

  dispose(): void {
    this.unmount();
    this.store?.dispose();
    this.store = null;
    this.context = null;
  }

  private requireContext(): EngineContext {
    if (!this.context) throw new Error('エンジンが未マウント');
    return this.context;
  }

  /** コンテナ寸法からモードとページ箱の大きさを決め、DOM へ反映する。 */
  private layout(): void {
    const context = this.requireContext();
    const host = this.stage?.parentElement;
    if (!host || !this.book || !this.left || !this.right) return;

    const container = { width: host.clientWidth, height: host.clientHeight };
    this.mode = resolveMode(context.mode, container, context.pages.length);
    const halves = this.mode === 'spread' ? 2 : 1;
    const size = pageSize(context.pages, this.currentPage);
    this.box = fitPageBox(container, size, halves);

    this.left.resize(this.box.width, this.box.height);
    this.right.resize(this.box.width, this.box.height);
    this.book.style.width = `${this.box.width * halves}px`;
    this.book.style.height = `${this.box.height}px`;
    this.book.classList.toggle(cls('book--single'), this.mode === 'single');
    this.book.classList.toggle(cls('book--hardcover'), context.hardcover);
    this.shift = this.mode === 'spread' ? spreadShift(this.current, this.box.width) : 0;
    this.applyTransform();
  }

  /** 本の変換（ズーム・パン・対向ページ無しの中央寄せ）。 */
  private transform(shift: number): string {
    return `translate(${this.pan.x + shift}px, ${this.pan.y}px) scale(${this.zoom})`;
  }

  private applyTransform(): void {
    if (!this.book) return;
    this.book.style.transform = this.transform(this.shift);
  }

  private bookWidth(): number {
    return this.box.width * (this.mode === 'spread' ? 2 : 1);
  }

  private bookBox(): { width: number; height: number } {
    return { width: this.bookWidth(), height: this.box.height };
  }

  /** ホストの寸法。未マウント・レイアウト前は null。 */
  private container(): { width: number; height: number } | null {
    const host = this.stage?.parentElement;
    if (!host || this.box.width === 0) return null;
    return { width: host.clientWidth, height: host.clientHeight };
  }

  /** 表示中のページが本ローカル座標で占める矩形。 */
  private slotRects(): PageSlotRect[] {
    const visible = this.visiblePages();
    const rects: PageSlotRect[] = [];
    const push = (page: number | null, offsetX: number): void => {
      if (page === null) return;
      rects.push({ page, rect: { x: offsetX, y: 0, w: this.box.width, h: this.box.height } });
    };
    push(visible.left, 0);
    push(visible.right, this.mode === 'spread' ? this.box.width : 0);
    return rects;
  }

  /** 拡大で生まれた余地の中に収める（本が視野の外へ飛ばないように）。 */
  private clampPan(pan: { x: number; y: number }): { x: number; y: number } {
    const limit = {
      x: (this.bookWidth() * (this.zoom - 1)) / 2,
      y: (this.box.height * (this.zoom - 1)) / 2,
    };
    return {
      x: Math.min(Math.max(pan.x, -limit.x), limit.x),
      y: Math.min(Math.max(pan.y, -limit.y), limit.y),
    };
  }

  private pixelRatio(): number {
    return Math.min(this.doc.defaultView?.devicePixelRatio ?? 1, MAX_DPR);
  }

  /**
   * 下地の物理ピクセル幅。ズームを掛けない — 拡大は領域ラスタ（detail）が担うので、
   * 下地をズームのたびに焼き直す必要はない（作り直さないぶんメモリも要求数も減る）。
   */
  private rasterWidth(): number {
    return Math.max(1, Math.round(this.box.width * this.pixelRatio()));
  }

  /** ページ全面換算での希望解像度（拡大時に領域ラスタが目指す値）。 */
  private effectiveWidth(): number {
    return this.box.width * this.pixelRatio() * this.zoom;
  }

  /** 実際に左右へ出すページ（単ページ表示では現在ページのみ）。 */
  private visiblePages(): { left: number | null; right: number | null } {
    if (this.mode === 'spread') return { left: this.current.left, right: this.current.right };
    return { left: this.currentPage, right: null };
  }

  /** 現在の見開きを描画し、テクスチャの保持範囲を更新する。 */
  private async paintCurrent(): Promise<void> {
    const store = this.store;
    if (!store || !this.left || !this.right) return;

    store.setWindow(this.windowPages());
    const visible = this.visiblePages();
    const width = this.rasterWidth();
    await Promise.all([
      this.paintSlot(this.left, visible.left, width),
      this.paintSlot(this.right, visible.right, width),
    ]);
    this.scheduleDetail();
  }

  /**
   * 領域ラスタを焼き直す予約。ズーム・パンが落ち着いてから 1 度だけ実行する。
   * めくり中は決して焼かない（下地だけが正しい絵）。
   */
  private scheduleDetail(): void {
    this.cancelDetail();
    if (this.flip || this.zoom < DETAIL_MIN_ZOOM) return;
    this.detailTimer = this.doc.defaultView?.setTimeout(() => {
      this.detailTimer = null;
      void this.paintDetail();
    }, DETAIL_DELAY_MS) as number | undefined ?? null;
  }

  /** 予約を取り消し、重ねていた領域ラスタを外す（＝必ず正しい下地の絵に戻る）。 */
  private cancelDetail(): void {
    if (this.detailTimer !== null) {
      this.doc.defaultView?.clearTimeout(this.detailTimer);
      this.detailTimer = null;
    }
    this.left?.clearDetail();
    this.right?.clearDetail();
    this.store?.clearDetails();
  }

  /** 可視領域だけを高解像度で焼いて下地の上へ重ねる（設計書 L361）。 */
  private async paintDetail(): Promise<void> {
    const context = this.context;
    const store = this.store;
    if (!context || !store || this.flip || this.zoom < DETAIL_MIN_ZOOM) return;

    const visible = this.visiblePages();
    const pages = [visible.left, visible.right].filter((no): no is number => no !== null);
    store.setDetailWindow(pages);

    const halves = this.mode === 'spread' ? 2 : 1;
    const book = { width: this.box.width * halves, height: this.box.height };
    const container = {
      width: this.stage?.parentElement?.clientWidth ?? book.width,
      height: this.stage?.parentElement?.clientHeight ?? book.height,
    };
    const view = visibleBox(container, book, this.zoom, this.pan, this.shift);

    const slots: [PageSlot | null, number | null, number][] = [
      [this.left, visible.left, 0],
      [this.right, visible.right, this.mode === 'spread' ? this.box.width : 0],
    ];

    await Promise.all(
      slots.map(async ([slot, pageNo, offsetX]) => {
        if (!slot || pageNo === null) return;
        const rect = { x: offsetX, y: 0, w: this.box.width, h: this.box.height };
        const region = pageRegion(rect, view);
        // 画面外のページ、および全面が見えているページは下地で足りる
        if (!region || isFullPage(region)) return;

        const size = pageSize(context.pages, pageNo);
        const plan = planRaster({
          effectiveWidth: this.effectiveWidth(),
          aspect: size.height / size.width,
          region: quantizeRegion(inflateRegion(region, REGION_MARGIN), REGION_STEP),
          maxEffectiveWidth: context.maxEffectiveWidth(pageNo),
          maxRasterWidth: context.maxRasterWidth(pageNo),
          maxArea: MAX_CANVAS_AREA,
          maxSide: MAX_CANVAS_SIDE,
        });

        const token = slot.beginDetail();
        const raster = await store.getDetail(pageNo, plan.targetWidth, plan.region);
        // await の間にめくり・ズーム解除が起きていたら重ねない（安全側）
        if (this.flip || this.zoom < DETAIL_MIN_ZOOM) return;
        slot.drawDetail(raster, token);
      }),
    );
  }

  private async paintSlot(slot: PageSlot, pageNo: number | null, width: number): Promise<void> {
    if (pageNo === null) {
      slot.clear();
      return;
    }
    // 表紙・裏表紙は hardcover 指定時に厚みのある見た目にする（FR-FLIP-07）。
    const pageCount = this.requireContext().pages.length;
    slot.setCover(pageNo === 1 || pageNo === pageCount);

    const token = slot.begin();
    const raster = await this.store?.get(pageNo, width);
    if (raster) slot.draw(raster, token);
  }

  /** 保持すべきページ（表示中＋近傍・NFR-PERF-01）。 */
  private windowPages(): number[] {
    const context = this.requireContext();
    const pages = new Set(pagesOf(this.current));
    pages.add(this.currentPage);
    for (let offset = 1; offset <= WINDOW_PAGES; offset += 1) {
      for (const no of [this.currentPage - offset, this.currentPage + offset]) {
        if (no >= 1 && no <= context.pages.length) pages.add(no);
      }
    }
    return [...pages];
  }

  /** めくりアニメーション無しの遷移（reduced-motion では短いフェードで代替）。 */
  private async simpleTransition(reduced: boolean): Promise<void> {
    this.layout();
    if (reduced && this.book) {
      this.book.animate([{ opacity: 0.4 }, { opacity: 1 }], { duration: REDUCED_MS });
    }
    await this.paintCurrent();
  }

  /** シートを 1 枚めくる。めくりの内訳（動く側・表裏・下に見えるページ）は共通層が決める。 */
  private async animateFlip(
    from: Spread,
    target: Spread,
    forward: boolean,
    fromPage: number,
  ): Promise<void> {
    const context = this.requireContext();
    const book = this.book;
    if (!book || !this.left || !this.right) return;

    const plan =
      this.mode === 'single'
        ? singleFlipPlan(fromPage, this.currentPage, forward, context.direction)
        : flipPlan(from, target, forward, context.direction);
    const { fromRight } = plan;

    // めくるシートに領域ラスタが乗ると絵が壊れる。めくりの間は下地だけを使う。
    this.cancelDetail();

    // 中央寄せは「めくる前」の状態から始め、着地後の位置へ動かす（表紙が開く動き）。
    const fromShift = this.mode === 'spread' ? spreadShift(from, this.box.width) : 0;
    this.layout();
    const width = this.rasterWidth();
    this.store?.setWindow(this.windowPages());
    await Promise.all([
      this.paintSlot(this.left, plan.under.left, width),
      this.paintSlot(this.right, plan.under.right, width),
    ]);

    const sheet = await this.buildSheet(plan.front, plan.back, width, fromRight);
    book.appendChild(sheet.el);

    if (fromShift !== this.shift) {
      book.animate(
        [{ transform: this.transform(fromShift) }, { transform: this.transform(this.shift) }],
        { duration: FLIP_MS, easing: 'cubic-bezier(.36,.15,.28,1)' },
      );
    }

    // 綴じ側は動かさない。戻るめくりは「倒れていた紙が起き上がる」＝逆再生で表す。
    const fallen = `rotateY(${fromRight ? -180 : 180}deg)`;
    const upright = 'rotateY(0deg)';
    this.flip = sheet.el.animate(
      plan.reverse
        ? [{ transform: fallen }, { transform: upright }]
        : [{ transform: upright }, { transform: fallen }],
      { duration: FLIP_MS, easing: 'cubic-bezier(.36,.15,.28,1)', fill: 'forwards' },
    );
    // 影は中間で最も濃くする（紙の湾曲の代替表現）。
    sheet.shade.animate([{ opacity: 0 }, { opacity: 0.35, offset: 0.5 }, { opacity: 0 }], {
      duration: FLIP_MS,
      easing: 'linear',
    });

    try {
      await this.flip.finished;
    } catch {
      // cancel（dispose・連続操作）による中断は静かに畳む。
    }
    this.flip = null;
    sheet.el.remove();

    await this.paintCurrent();
  }

  /** めくりシート（表裏 2 面＋影）を作る。 */
  private async buildSheet(
    frontPage: number | null,
    backPage: number | null,
    width: number,
    fromRight: boolean,
  ): Promise<{ el: HTMLDivElement; shade: HTMLDivElement }> {
    const el = this.doc.createElement('div');
    el.className = `${cls('sheet')} ${cls(fromRight ? 'sheet--right' : 'sheet--left')}`;
    el.style.width = `${this.box.width}px`;
    el.style.height = `${this.box.height}px`;

    const front = new PageSlot(this.doc, 'front');
    const back = new PageSlot(this.doc, 'back');
    front.resize(this.box.width, this.box.height);
    back.resize(this.box.width, this.box.height);

    const shade = this.doc.createElement('div');
    shade.className = cls('shade');
    el.append(front.el, back.el, shade);

    await Promise.all([
      this.paintSlot(front, frontPage, width),
      this.paintSlot(back, backPage, width),
    ]);
    return { el, shade };
  }
}
